import { createAsyncThunk, createEntityAdapter, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { LocalProperty, LocalPropertyValue } from '@cdaas/api-clients/dist/product-service-openapi';

import { RootState } from '.';
import { productServicePropertiesClient } from '../api-client/product-service-client';
import { EntriesState, initialEntriesState, Loading } from '../utils/store';
import { resolveAxiosErrorResponse } from '../utils/errors';

type PropertiesState = EntriesState<LocalProperty>;

export const initialAdminPropertiesState: PropertiesState = initialEntriesState;

const adminPropertiesAdapter = createEntityAdapter<LocalProperty>();

export const fetchAdminProperties = createAsyncThunk('adminProperties/fetchAdminPropertiesStatus', async (_, { rejectWithValue }) => {
  try {
    const response = await productServicePropertiesClient.getProperties();
    return response.data.properties;
  } catch (error) {
    return rejectWithValue(resolveAxiosErrorResponse(error)?.statusText);
  }
});

const adminProperties = createSlice({
  name: 'adminProperties',
  initialState: adminPropertiesAdapter.getInitialState(initialAdminPropertiesState),
  reducers: {
    addPropertyValue(state, action: PayloadAction<{ propertyId: number; value: LocalPropertyValue }>) {
      const property = state.entities[action.payload.propertyId];
      if (property) {
        property.property_values = [...(property.property_values ?? []), action.payload.value];
      }
    },
  },
  extraReducers: builder => {
    builder.addCase(fetchAdminProperties.pending, state => {
      state.loading = Loading.PENDING;
    });
    builder.addCase(fetchAdminProperties.fulfilled, (state, action) => {
      if (action.payload) {
        adminPropertiesAdapter.setAll(state, action.payload);
        state.loading = Loading.SUCCEEDED;
      }
    });
    builder.addCase(fetchAdminProperties.rejected, state => {
      state.loading = Loading.FAILED;
    });
  },
});

export const { addPropertyValue } = adminProperties.actions;

export const { selectAll: selectAllProperties } = adminPropertiesAdapter.getSelectors((state: RootState) => state.adminProperties);

export const selectAdminPropertiesLoading = (state: RootState) => state.adminProperties.loading;

export default adminProperties.reducer;
